/**
 * VibeCheck CLI v2 — Config Loading
 */

import * as fs from 'fs';
import * as path from 'path';
import type { VibecheckConfig } from '../types';

const CONFIG_FILES = ['.vibecheckrc.json', '.vibecheckrc', 'vibecheck.config.json'];

const DEFAULT_CONFIG: VibecheckConfig = {
  version: '1.0',
  project: {
    name: 'auto-detect',
    framework: 'auto-detect',
    language: 'auto-detect',
    packageManager: 'auto-detect',
  },
  scan: {
    include: ['src/**/*', 'app/**/*', 'pages/**/*', 'lib/**/*', 'components/**/*'],
    exclude: ['node_modules', 'dist', '.next', 'build', 'coverage', '.git', '*.min.js'],
    severity: 'low',
    categories: ['hallucination', 'security', 'quality'],
  },
  firewall: {
    enabled: true,
    strict: false,
  },
  output: {
    format: 'pretty',
    color: true,
  },
};

export function loadConfig(projectPath: string, customPath?: string): VibecheckConfig {
  const config = { ...DEFAULT_CONFIG };

  // Try custom path first
  if (customPath) {
    const abs = path.resolve(projectPath, customPath);
    if (fs.existsSync(abs)) {
      try {
        const raw = JSON.parse(fs.readFileSync(abs, 'utf-8'));
        return mergeConfig(config, raw);
      } catch { /* fall through */ }
    }
  }

  // Try standard config files
  for (const file of CONFIG_FILES) {
    const abs = path.join(projectPath, file);
    if (fs.existsSync(abs)) {
      try {
        const raw = JSON.parse(fs.readFileSync(abs, 'utf-8'));
        return mergeConfig(config, raw);
      } catch { /* fall through */ }
    }
  }

  return config;
}

export function writeConfig(projectPath: string, config: VibecheckConfig): string {
  const filePath = path.join(projectPath, '.vibecheckrc.json');
  fs.writeFileSync(filePath, JSON.stringify(config, null, 2) + '\n', 'utf-8');
  return filePath;
}

function mergeConfig(base: VibecheckConfig, override: Partial<VibecheckConfig>): VibecheckConfig {
  return {
    ...base,
    ...override,
    project: { ...base.project, ...(override.project || {}) },
    scan: { ...base.scan, ...(override.scan || {}) },
    firewall: { ...base.firewall, ...(override.firewall || {}) },
    output: { ...base.output, ...(override.output || {}) },
  } as VibecheckConfig;
}

export function ensureVibecheckDir(projectPath: string): string {
  const dir = path.join(projectPath, '.vibecheck');
  if (!fs.existsSync(dir)) {
    fs.mkdirSync(dir, { recursive: true });
  }
  return dir;
}
